/* global window, videojs */

import whenDomReady from 'when-dom-ready';
import adblockDetect from 'adblock-detect';
import canAutoPlay from 'can-autoplay';
import loadJS from 'load-js';
import get from 'lodash/get';

import EncoreInjector from './EncoreInjector';
import settings from './settings';
import { checkWebPFeature } from './utils';
import { isStickyActive } from './stickyplayer';

/**
 * Check if any sort of ad blocking extension is enabled.
 * @returns {Promise<boolean>} ad block detected.
 */
function detectAdBlock() {
  return new Promise(resolve => {
    adblockDetect(adBlockDetected => {
      resolve(adBlockDetected);
    });
  });
}

/**
 * Check if video autoplay is allowed.
 * @returns {Promise<boolean>} autoplay allowed.
 */
function detectAutoplay() {
  return new Promise(resolve => {
    canAutoPlay
      .video({
        timeout: 500,
        inline: true,
      })
      .then(({ result, error }) => {
        videojs.log(`canAutoPlay = [${result}]`);

        if (result === false) {
          videojs.log.warn(`canAutoPlay error: [${error}]`);
        }

        settings.canAutoplay = result;
        resolve(result);
      });
  });
}

/**
 * Load external player scripts.
 * If ad block is enabled, don't even attempt to load player scripts.
 * If disabled, try to load the default scripts. If default scripts fail,
 * try loading the fallback scripts. Resolves on any succesful load and
 * rejects if all attempts failed.
 * @param {boolean} adBlockDetected - ad block detected.
 * @returns {Promise} resolves when script is loaded successfully, rejects otherwise.
 */
function loadScripts(adBlockDetected) {
  videojs.log(`adBlockDetected = [${adBlockDetected}]`);

  if (adBlockDetected) {
    return adBlockDetected;
  }

  return new Promise((resolve, reject) => {
    loadJS(settings.defaultScripts)
      .then(() => resolve())
      .catch(() => {
        loadJS(settings.fallbackScripts)
          .then(() => resolve())
          .catch(error => reject(error));
      });
  });
}

/**
 * Detect WebP support.
 */
function detectWebP() {
  return new Promise(resolve => {
    checkWebPFeature('animation', (feature, result) => {
      if (feature === 'animation') {
        settings.canUseWebP = result;
      }
      videojs.log(`canUseWebP = [${settings.canUseWebP}]`);
      resolve(true);
    });
  });
}

/**
 * Kickstart video player. Insert player variable into global context.
 */
function start() {
  settings.imaSdkAvailable = typeof get(window, 'google.ima') !== 'undefined';
  videojs.log(`imaSdkAvailable = [${settings.imaSdkAvailable}]`);
  videojs.log(`isStickyActive = [${isStickyActive()}]`);

  window.hearstPlayer = new EncoreInjector(settings);
  window.hearstPlayer.initialize(window);
}

/**
 * Perform all pre-player initialization tasks.
 * Once finished, kickstart video player.
 */
function bootstrap() {
  whenDomReady()
    .then(() => detectAutoplay())
    .then(() => detectAdBlock())
    .then(adBlockDetected => loadScripts(adBlockDetected))
    .catch(error => videojs.log.error(error))
    .then(() => detectWebP())
    .then(() => start());
}

export default bootstrap;



// WEBPACK FOOTER //
// ./src/bootstrap.js